// AddClient.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import './addClient.css';
import axios from 'axios';
import LoadingPopup from '../../SharedView/LoadingPopup';
import { StagedClientDocuments, uploadClientDocuments } from './ClientDocuments';

function AddClient() {
  const [newClient, setNewClient] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    display_name: '',
    email: '',
    contact_number: '',
    primary_communication_method: '',
    trn_number: '',
    all_properties_notes: '',
    is_vat_registered: false,
    client_type: '',
    address_line_1: '',
    city: '',
    state: '',
    country: '',
    account_manager_id: null,
    account_coordinator_id: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [adminOptions, setAdminOptions] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  // Staged documents — uploaded to the new client after it's created.
  const [docFiles, setDocFiles] = useState({ trade_license: null, vat_certificate: null });
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Arrived from a lead ("Register client" in the leads drawer).
   *
   * Carried in router state rather than the query string: it is somebody's
   * name, number and email, and those do not belong in a URL. On save the new
   * client is linked back to the lead and the lead becomes Registered, so the
   * pipeline closes itself instead of relying on somebody remembering.
   */
  const fromLead = location.state?.fromLead || null;

  useEffect(() => {
    if (!fromLead) return;
    const { id, ...prefill } = fromLead;
    setNewClient((c) => ({
      ...c,
      ...Object.fromEntries(Object.entries(prefill).filter(([, v]) => v)),
      primary_communication_method: c.primary_communication_method || 'WhatsApp',
    }));
    // Prefilled once, from the state we arrived with; editing is the point of
    // landing on the form rather than creating the client from the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientTypeOptions = [
    { value: 'business', label: 'Business' },
    { value: 'individual', label: 'Individual' }
  ];

  const stateOptions = [
    { value: 'Dubai', label: 'Dubai' },
    { value: 'Abu Dhabi', label: 'Abu Dhabi' },
    { value: 'Sharjah', label: 'Sharjah' },
    { value: 'Ajman', label: 'Ajman' },
    { value: 'Ras Al Khaimah', label: 'Ras Al Khaimah' },
    { value: 'Fujairah', label: 'Fujairah' },
    { value: 'Umm Al Quwain', label: 'Umm Al Quwain' }
  ];

  const countryOptions = [
    { value: 'United Arab Emirates', label: 'United Arab Emirates' }
  ];

  useEffect(() => {
    const fetchAdmins = async () => {
      setAdminsLoading(true);
      try {
        const res = await axios.get('/api/team/role', {
          params: { role: 'admin' },
          withCredentials: true,
        });

        const rows = Array.isArray(res.data) ? res.data : [];

        const opts = rows.map((u) => ({
          value: u.id,
          label: `${u.first_name || ''} ${u.last_name || ''}`.trim() + (u.username ? ` (${u.username})` : ''),
        }));

        setAdminOptions(opts);
      } catch (err) {
        console.error('Error fetching admins:', err);
        setAdminOptions([]);
      } finally {
        setAdminsLoading(false);
      }
    };

    fetchAdmins();
  }, []);

  // Auto-generate display name
  useEffect(() => {
    if (newClient.company_name) {
      setNewClient((prevClient) => ({
        ...prevClient,
        display_name: newClient.company_name.length >= 4 ? newClient.company_name : '',
      }));
    } else {
      const generatedName = `${newClient.first_name} ${newClient.last_name}`.trim();
      setNewClient((prevClient) => ({
        ...prevClient,
        display_name: generatedName.length >= 4 ? generatedName : '',
      }));
    }
  }, [newClient.first_name, newClient.last_name, newClient.company_name]);

  const handleAddClient = async (e) => {
    e.preventDefault();

    if (newClient.display_name.trim().length < 4) {
      alert('Display Name must be at least 4 characters long');
      return;
    }

    if (!newClient.client_type) {
      alert('Please select a client type.');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        ...newClient,
        email: (newClient.email || '').trim().toLowerCase(),
      };

      const response = await axios.post('/api/clients', payload);

      if (response.status === 201) {
        // Upload any staged documents to the newly-created client.
        const newClientId = response.data?.clientId;
        if (newClientId && (docFiles.trade_license || docFiles.vat_certificate)) {
          try {
            await uploadClientDocuments(newClientId, docFiles);
          } catch (docErr) {
            console.error('Client created but document upload failed:', docErr);
          }
        }
        if (fromLead?.id && newClientId) {
          try {
            await axios.put(`/api/leads/${fromLead.id}`, {
              client_id: newClientId,
              status: 'registered',
            });
          } catch (leadErr) {
            // The client exists; a broken link is a smaller problem than a
            // rolled-back creation, so it is reported and not raised.
            console.error('Client created but the lead was not linked:', leadErr);
          }
        }
        setFeedbackMessage('Client added successfully!');
        setTimeout(() => {
          navigate(fromLead?.id ? '/leads' : '/clients');
        }, 2000);
      } else {
        setFeedbackMessage('Error adding client.');
        console.error('Error adding client:', response);
      }
    } catch (error) {
      setFeedbackMessage('Error adding client.');
      console.error('Error adding client:', error.response ? error.response.data : error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="client-form-page">
      <div className="client-form-header">
        <div>
          <h1 className="client-form-title">Add New Client</h1>
          <div className="client-form-subtitle">Create a client profile, billing info, and admin allocation (optional).</div>
        </div>
      </div>

      <form className="client-form" onSubmit={handleAddClient}>
        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Personal Information</h2>
            <p className="client-form-card-subtitle">Primary contact details for this client.</p>
          </div>

          <div className="client-form-grid">
            <div className="client-form-group">
              <label className="client-form-label">Primary Contact First Name</label>
              <input
                className="client-form-input"
                type="text"
                value={newClient.first_name}
                onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                placeholder="Primary contact first name"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Primary Contact Last Name</label>
              <input
                className="client-form-input"
                type="text"
                value={newClient.last_name}
                onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                placeholder="Primary contact last name"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Company Name</label>
              <input
                className="client-form-input"
                type="text"
                value={newClient.company_name}
                onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                placeholder="Leave empty if not applicable"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Display Name</label>
              <input
                className="client-form-input"
                type="text"
                value={newClient.display_name}
                onChange={(e) => setNewClient({ ...newClient, display_name: e.target.value })}
                required
                minLength={4}
                placeholder="First and last name or short company name"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Email</label>
              <input
                className="client-form-input"
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                placeholder="example@email.com"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Primary Contact Number</label>
              <PhoneInput
                defaultCountry="ae"
                value={newClient.contact_number}
                onChange={(value) =>
                  setNewClient((prev) => ({
                    ...prev,
                    contact_number: value,
                  }))
                }
                placeholder="Enter contact number"
                className="client-phone-wrapper"
                inputClassName="client-phone-input"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">WhatsApp Number or Group Name</label>
              <input
                className="client-form-input"
                type="text"
                value={newClient.primary_communication_method}
                onChange={(e) => setNewClient({ ...newClient, primary_communication_method: e.target.value })}
                placeholder="WhatsApp group name or phone number"
              />
              <div className="client-form-helper">For example: "PRHO Signature Holiday Homes” or +971…</div>
            </div>
          </div>
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Admin Allocation</h2>
            <p className="client-form-card-subtitle">Assign admins responsible for this client.</p>
          </div>

          <div className="client-form-grid">
            <div className="client-form-group">
              <label className="client-form-label">Account Manager</label>
              <Select
                classNamePrefix="hhc-select"
                options={adminOptions}
                value={adminOptions.find((o) => o.value === newClient.account_manager_id) || null}
                onChange={(selectedOption) =>
                  setNewClient((prev) => ({
                    ...prev,
                    account_manager_id: selectedOption ? selectedOption.value : null,
                  }))
                }
                placeholder={adminsLoading ? 'Loading admins...' : 'Select Account Manager'}
                isClearable
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Account Coordinator</label>
              <Select
                classNamePrefix="hhc-select"
                options={adminOptions}
                value={adminOptions.find((o) => o.value === newClient.account_coordinator_id) || null}
                onChange={(selectedOption) =>
                  setNewClient((prev) => ({
                    ...prev,
                    account_coordinator_id: selectedOption ? selectedOption.value : null,
                  }))
                }
                placeholder={adminsLoading ? 'Loading admins...' : 'Select Account Coordinator'}
                isClearable
              />
            </div>
          </div>
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Additional Details</h2>
            <p className="client-form-card-subtitle">VAT and client type information.</p>
          </div>

          <div className="client-form-grid">
            <div className="client-form-group">
              <label className="client-form-label">Client Type</label>
              <Select
                classNamePrefix="hhc-select"
                options={clientTypeOptions}
                value={clientTypeOptions.find((option) => option.value === newClient.client_type) || null}
                onChange={(selectedOption) => setNewClient({ ...newClient, client_type: selectedOption.value })}
                placeholder="Select Client Type"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">VAT Registered</label>
              <div className="client-form-toggle-row">
                <label className="client-form-switch">
                  <input
                    type="checkbox"
                    checked={newClient.is_vat_registered}
                    onChange={(e) => setNewClient({ ...newClient, is_vat_registered: e.target.checked })}
                  />
                  <span className="client-form-slider" />
                </label>
                <span className="client-form-toggle-text">
                  {newClient.is_vat_registered ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            <div className="client-form-group">
              <label className="client-form-label" style={{ visibility: newClient.is_vat_registered ? 'visible' : 'hidden' }}>
                TRN Number
              </label>
              <input
                className="client-form-input"
                type="text"
                value={newClient.trn_number}
                onChange={(e) => setNewClient({ ...newClient, trn_number: e.target.value })}
                style={{ visibility: newClient.is_vat_registered ? 'visible' : 'hidden' }}
              />
            </div>
          </div>
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Notes for All Properties</h2>
            <p className="client-form-card-subtitle">These notes will apply to all properties under this client.</p>
          </div>

          <div className="client-form-group">
            <label className="client-form-label">Property Notes</label>
            <textarea
              className="client-form-textarea"
              value={newClient.all_properties_notes}
              onChange={(e) => setNewClient({ ...newClient, all_properties_notes: e.target.value })}
              placeholder="For example: keep AC at 22 degrees for all units."
              rows="5"
            />
          </div>
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Documents</h2>
            <p className="client-form-card-subtitle">Trade license and TRN / VAT certificate (optional). Uploaded once the client is created.</p>
          </div>
          <StagedClientDocuments files={docFiles} setFiles={setDocFiles} />
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Billing Information</h2>
            <p className="client-form-card-subtitle">Billing address used for invoices.</p>
          </div>

          <div className="client-form-grid">
            <div className="client-form-group">
              <label className="client-form-label">Country</label>
              <Select
                classNamePrefix="hhc-select"
                options={countryOptions}
                value={countryOptions.find((option) => option.value === newClient.country) || null}
                onChange={(selectedOption) => setNewClient({ ...newClient, country: selectedOption.value })}
                placeholder="Select Country"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">State/Emirate</label>
              <Select
                classNamePrefix="hhc-select"
                options={stateOptions}
                value={stateOptions.find((option) => option.value === newClient.state) || null}
                onChange={(selectedOption) => setNewClient({ ...newClient, state: selectedOption.value })}
                placeholder="Select State"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Address Line 1</label>
              <input
                className="client-form-input"
                type="text"
                value={newClient.address_line_1}
                onChange={(e) => setNewClient({ ...newClient, address_line_1: e.target.value })}
                placeholder="Office / Address"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">City</label>
              <input
                className="client-form-input"
                type="text"
                value={newClient.city}
                onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                placeholder="City"
              />
            </div>
          </div>
        </section>

        <div className="client-form-actions">
          <button className="client-form-primary-button" type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </form>

      <LoadingPopup
        isLoading={isLoading}
        feedbackMessage={feedbackMessage}
        onClose={() => setFeedbackMessage('')}
      />
    </div>
  );
}

export default AddClient;